/**
 * PRP-MCP-5: everything the estimate page needs for chat edits, in one hook so
 * V2SupplementPage only wires it in: the change log and version polling
 * ({@link useChatChanges}), the "Only chat changes" filter, the "from chat"
 * marks context value and the Chat changes panel. There is no review step
 * before export (founder decision D2, 2026-10-11).
 */
import { useCallback, useMemo, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import { BackendAPI } from '../../lib/api';
import type { ParsedEstimateResponse } from '../../types';
import ChatChangesPanel from '../components/ChatChangesPanel';
import { payloadVersionOf } from './chatChanges';
import type { ChatMarks } from './chatChanges';
import { useChatChanges } from './useChatChanges';

export function useEstimateChat(
  result: ParsedEstimateResponse | null,
  setResult: Dispatch<SetStateAction<ParsedEstimateResponse | null>>,
) {
  const estimateId = result?.parse_hash;
  const [onlyChat, setOnlyChat] = useState(false);

  const reloadEstimate = useCallback(async () => {
    if (!estimateId) return;
    try {
      const res = await BackendAPI.getImport(estimateId);
      // Merge: a fresh parse carries fields (export cost) the stored read does not.
      setResult((prev) => (prev && prev.parse_hash === res.parse_hash ? { ...prev, ...res } : prev));
    } catch {
      // Keep what is on screen; the next tick tries again.
    }
  }, [estimateId, setResult]);

  const chat = useChatChanges({
    estimateId,
    loadedVersion: payloadVersionOf(result),
    reloadEstimate,
  });

  const marks: ChatMarks = useMemo(
    () => ({ index: chat.index, onlyChat: onlyChat && !!chat.data }),
    [chat.index, chat.data, onlyChat],
  );

  const panel = chat.data ? (
    <ChatChangesPanel
      data={chat.data}
      onUndoChange={chat.undoChange}
      onUndoBatch={chat.undoBatch}
      onlyChat={onlyChat}
      onOnlyChatChange={setOnlyChat}
    />
  ) : null;

  // PRP-INTAKE-1: after an open item is answered on the page, re-read the estimate and the log.
  const { refresh } = chat;
  const reload = useCallback(async () => {
    await reloadEstimate();
    await refresh();
  }, [reloadEstimate, refresh]);

  return { marks, panel, reload };
}
